import React from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { apiGetCetakNota, formatApiErrorDetail } from "@/lib/api";

// Customer receipt (thermal 58/80 mm from Pengaturan). Prices yes, HPP never
// (the server does not send it). ?pembayaran=<id> highlights that payment's
// cash received / change; otherwise the latest payment is shown.
const rp = (n) => Number(n || 0).toLocaleString("id-ID");
const m = (cm) => (cm / 100).toLocaleString("id-ID");
// "2 x 2x1 m = 4 m2 x 25.000" for area-priced lines, "2 PCS x 15.000" otherwise.
// hotfix 2026-10-09: "panjang 1,3 m, ditagih 1,5 m lari x 100.000" for per-metre lines
const rincian = (it) => (it.mode_harga === "PER_METER_LARI" && it.panjang_ditagih_cm != null
  ? `panjang ${m(it.panjang_nyata_cm)} m, ditagih ${m(it.panjang_ditagih_cm)} m lari x ${rp(it.harga)}`
  : it.mode_harga === "PER_M2" && it.p_cm
  ? `${it.quantity} x ${m(it.p_cm)}x${m(it.l_cm)} m = ${((it.p_cm * it.l_cm * it.quantity) / 10000).toLocaleString("id-ID")} m2 x ${rp(it.harga)}`
  : `${it.quantity} ${it.satuan || ""} x ${rp(it.harga)}`);
const STATUS = { BELUM_BAYAR: "BELUM BAYAR", DP: "DP", LUNAS: "LUNAS" };

export default function CetakNota() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const [d, setD] = React.useState(null);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    apiGetCetakNota(id, params.get("pembayaran"))
      .then(setD)
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, [id, params]);

  if (error) return <div data-testid="nota-cetak-error" className="p-6 text-red-600">{error}</div>;
  if (!d) return <div className="p-6 text-slate-500">Memuat…</div>;

  const fmt = (iso) => new Intl.DateTimeFormat("id-ID", { timeZone: d.zona_waktu, dateStyle: "short", timeStyle: "short" })
    .format(iso ? new Date(iso) : new Date());
  const w = d.lebar_thermal_mm;
  const p = d.sorotan;
  const line = (label, value, testid, bold) => (
    <div className={`flex justify-between gap-2 ${bold ? "font-bold" : ""}`}><span>{label}</span><span data-testid={testid}>{value}</span></div>
  );

  return (
    <div className="min-h-screen bg-slate-200 print:bg-white py-6 print:py-0">
      <style>{`@page { size: ${w}mm auto; margin: 0; } @media print { .no-print { display: none !important; } }`}</style>
      <div className="no-print mx-auto mb-4 flex justify-center gap-3">
        <button data-testid="nota-cetak-print" onClick={() => window.print()} className="rounded bg-soq-biru px-4 py-2 text-sm text-white">Cetak</button>
        <span className="self-center text-xs text-slate-600">Kertas {w} mm (ubah di Pengaturan)</span>
      </div>
      <div data-testid="nota-paper" data-width={w} className="mx-auto bg-white text-black font-mono leading-snug p-[3mm]"
        style={{ width: `${w}mm`, fontSize: w === 58 ? "8.5pt" : "10pt" }}>
        <div className="text-center font-bold" data-testid="nota-usaha">{d.nama_usaha}</div>
        <div className="text-center border-b border-black border-dashed pb-1 mb-1">NOTA</div>
        {d.status_nota === "CANCELLED" && (
          <div data-testid="nota-dibatalkan" className="text-center font-bold border-2 border-black my-1 py-0.5">*** DIBATALKAN ***</div>
        )}
        {line("No", d.nomor_nota, "nota-nomor")}
        {line("Tgl", fmt(d.tanggal))}
        {line("Kasir", d.kasir, "nota-kasir")}
        {line("Cust", `${d.customer?.kode_customer || ""} ${d.customer?.nama || ""}`.trim(), "nota-customer")}
        <div className="border-t border-black border-dashed my-1" />
        {d.items.map((it, i) => (
          <div key={i} data-testid={`nota-item-${i}`} className="mb-1">
            <div>{it.nama}</div>
            <div className="flex justify-between gap-2">
              <span data-testid={`nota-item-rincian-${i}`}>{rincian(it)}</span>
              <span>{rp(it.bruto ?? it.jumlah)}</span>
            </div>
            {it.disc > 0 && line("  disc", `-${rp(it.disc)}`)}
          </div>
        ))}
        <div className="border-t border-black border-dashed my-1" />
        {(d.diskon > 0 || d.ppn) && line("Subtotal", rp(d.subtotal))}
        {d.diskon > 0 && line("Diskon", `-${rp(d.diskon)}`)}
        {d.ppn && d.ppn.mode !== "INKLUSIF" && line("DPP", rp(d.ppn.dpp))}
        {d.ppn && line(`PPN ${d.ppn.tarif}%${d.ppn.mode === "INKLUSIF" ? " (termasuk)" : ""}`, rp(d.ppn.ppn), "nota-ppn")}
        {line("TOTAL", rp(d.total), "nota-total", true)}
        {line("Dibayar", rp(d.dibayar), "nota-dibayar")}
        {!d.pembatalan && line("Sisa", rp(d.sisa), "nota-sisa", true)}
        {line("Status", d.pembatalan ? "DIBATALKAN" : STATUS[d.status_bayar] || d.status_bayar, "nota-status")}
        {p && (
          <div data-testid="nota-sorotan" className="border-t border-black border-dashed mt-1 pt-1">
            <div>Bayar {fmt(p.tanggal)}</div>
            {p.tunai_diterima > 0 && line("Tunai diterima", rp(p.tunai_diterima))}
            {p.non_tunai > 0 && line("Non tunai", rp(p.non_tunai))}
            {p.non_tunai > 0 && p.rekening && <div data-testid="nota-rekening" className="break-words">ke {p.rekening.bank} {p.rekening.nomor} a.n. {p.rekening.atas_nama}</div>}
            {line("Kembalian", rp(p.kembalian), "nota-kembalian", true)}
            {p.dikoreksi && <div>(pembayaran ini telah dikoreksi)</div>}
          </div>
        )}
        {d.pembatalan && (
          <div data-testid="nota-pembatalan" className="border-t border-black border-dashed mt-1 pt-1">
            <div>Batal {fmt(d.pembatalan.waktu)}</div>
            {line("Dibayar", rp(d.pembatalan.dibayar_saat_batal))}
            {line("Dikembalikan", rp(d.pembatalan.dikembalikan), "nota-dikembalikan")}
            {d.pembatalan.tidak_dikembalikan > 0 && line("Tdk dikembalikan", rp(d.pembatalan.tidak_dikembalikan), "nota-tidak-dikembalikan")}
            {d.pembatalan.saldo > 0 && line("Belum selesai", rp(d.pembatalan.saldo), "nota-saldo-kembali", true)}
          </div>
        )}
        {d.tracking_url && (
          <div className="flex flex-col items-center mt-2">
            <QRCodeCanvas data-testid="nota-qr" value={d.tracking_url} size={w === 58 ? 96 : 120} />
            <div style={{ fontSize: "0.8em" }}>Lacak pesanan Anda</div>
          </div>
        )}
        <div className="text-center mt-2" style={{ fontSize: "0.8em" }}>Dicetak {fmt(null)}</div>
      </div>
    </div>
  );
}
